import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
  Loader2, RefreshCw, Car, ParkingCircle, AlertTriangle, Wallet, Gauge,
  Users, ClipboardList, PackageCheck, ShieldCheck, Building2, ChevronRight,
  LayoutDashboard, TrendingUp, History, Search, X, Info, Zap, LogIn,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import DashboardLayout from "@/components/DashboardLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { IngresosContent, CortesiasPanel, GastosHoyPanel } from "@/components/DashboardIngresos";
import { HistorialTab } from "@/components/DashboardHistorial";
import { slaTier, slaLabel, minsDesde } from "@/domain/valetSla";

const REFRESH_MS = 30000;
const INITIAL_CARDS = 6;

const TABS = [
  { key: "operacion", label: "Operación ahora", icon: LayoutDashboard, ready: true },
  { key: "ingresos", label: "Ingresos de hoy", icon: TrendingUp, ready: true },
  { key: "historial", label: "Historial y métricas", icon: History, ready: true },
];

// Deprecación de 'Recibido' (Jun 2026): el flujo Valet tiene 4 estados visibles y el conteo de
// vehículos bajo custodia vive en "En resguardo" (el backend pliega ahí cualquier heredado).
const VALET_SEG = [
  { key: "valet_resguardo", label: "En resguardo", color: "#3B82F6" },
  { key: "valet_solicitado", label: "Solicitados", color: "#F59E0B" },
  { key: "valet_en_entrega", label: "En entrega", color: "#8B5CF6" },
];

const fmtHace = (d) => {
  if (!d) return "—";
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `hace ${s} s`;
  const m = Math.floor(s / 60);
  return `hace ${m} min`;
};
const tierBadge = {
  red: "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]",
  orange: "bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]",
  green: "bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]",
};

/* ---------- Tarjeta KPI superior ---------- */
function KpiCard({ icon: Icon, label, value, sub, subTone = "muted", onClick, active, alert, testid, children }) {
  const clickable = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      data-testid={testid}
      className={`text-left rounded-2xl border p-4 shadow-sm transition-all ${
        alert ? "border-[#FCA5A5] ring-1 ring-[#FCA5A5]" : active ? "border-[#FFCC00] ring-2 ring-[#FFCC00]/60" : "border-[#e4e4e7]"
      } bg-white ${clickable ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-[11px] tracking-[0.1em] uppercase font-bold text-[#52525B]">
          <Icon className="h-4 w-4 text-[#B45309]" strokeWidth={2.3} /> {label}
        </span>
        {clickable && <ChevronRight className="h-4 w-4 text-[#A1A1AA]" />}
      </div>
      {children ? (
        <div className="mt-2">{children}</div>
      ) : (
        <>
          <p className="mt-2 font-display text-4xl font-black leading-none text-[#0A0A0A]" data-testid={testid ? `${testid}-value` : undefined}>{value}</p>
          {sub && (
            <p className={`mt-1.5 text-xs font-semibold ${subTone === "alert" ? "text-[#DC2626] flex items-center gap-1" : "text-[#71717A]"}`}>
              {subTone === "alert" && <AlertTriangle className="h-3 w-3" />}{sub}
            </p>
          )}
        </>
      )}
    </button>
  );
}

/* ---------- Anillo de ocupación ---------- */
function DonutRing({ pct }) {
  const r = 26, c = 2 * Math.PI * r;
  const p = Math.min(100, Math.max(0, pct || 0));
  const color = p >= 100 ? "#EF4444" : p >= 85 ? "#F59E0B" : "#16A34A";
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" className="shrink-0">
      <circle cx="36" cy="36" r={r} fill="none" stroke="#EEF0F2" strokeWidth="7" />
      <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
        strokeDasharray={`${(p / 100) * c} ${c}`} transform="rotate(-90 36 36)" />
      <text x="36" y="40" textAnchor="middle" className="fill-[#0A0A0A]" style={{ fontSize: 15, fontWeight: 800 }}>{p}%</text>
    </svg>
  );
}

/* ---------- Barra segmentada de estados valet ---------- */
function SegmentedBar({ t }) {
  const total = VALET_SEG.reduce((a, s) => a + (t?.[s.key] || 0), 0);
  return (
    <div className="rounded-2xl border border-[#e4e4e7] bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="h-4 w-4 text-[#B45309]" strokeWidth={2.3} />
        <p className="text-[11px] tracking-[0.12em] uppercase font-bold text-[#52525B]">Estados de Valet</p>
      </div>
      <p className="text-xs text-[#A1A1AA] mb-4">Distribución de vehículos por estado actual.</p>
      <div className="flex h-3.5 w-full overflow-hidden rounded-full bg-[#EEF0F2]" data-testid="valet-segmented-bar">
        {total > 0 ? VALET_SEG.map((s) => {
          const val = t?.[s.key] || 0;
          if (val <= 0) return null;
          return <div key={s.key} style={{ width: `${(val / total) * 100}%`, backgroundColor: s.color }} title={`${s.label}: ${val}`} />;
        }) : null}
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {VALET_SEG.map((s) => (
          <div key={s.key} data-testid={`kpi-${s.key.replace(/_/g, "-")}`}>
            <span className="flex items-center gap-1.5 text-[11px] font-bold text-[#52525B]">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} /> {s.label}
            </span>
            <p className="mt-1 font-display text-2xl font-black text-[#0A0A0A]">{t?.[s.key] ?? 0}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Mini-métrica dentro de la tarjeta de sucursal ---------- */
function Mini({ icon: Icon, value, label, alert, alertSub, tid }) {
  return (
    <div className="flex flex-col items-start min-w-[64px]" data-testid={tid}>
      <Icon className={`h-4 w-4 ${alert ? "text-[#DC2626]" : "text-[#B45309]"}`} strokeWidth={2.3} />
      <p className="mt-1.5 font-display text-xl font-black text-[#0A0A0A] leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-[#71717A]">{label}</p>
      {alert && alertSub && (
        <p className="mt-0.5 text-[10px] font-bold text-[#DC2626] flex items-center gap-0.5"><AlertTriangle className="h-2.5 w-2.5" />{alertSub}</p>
      )}
    </div>
  );
}

/* ---------- Tarjeta de sucursal ---------- */
function SucursalCard({ s }) {
  const operando = s.cajas_abiertas > 0;
  const esValet = s.valet_activo || s.valet_activos > 0;
  const esEstancia = s.modelo_cobro !== "SOLO_VALET";
  return (
    <div
      data-testid={`suc-card-${s.id_sucursal}`}
      data-alert={s.tiene_alerta ? "1" : "0"}
      className={`rounded-2xl border bg-white p-4 shadow-sm transition-all hover:shadow-md ${s.tiene_alerta ? "border-[#FCA5A5] ring-1 ring-[#FCA5A5]" : "border-[#e4e4e7]"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="h-4 w-4 text-[#3F3F46] shrink-0" />
          <p className="font-display font-black text-[#0A0A0A] truncate">{s.nombre}</p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${operando ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#F4F4F5] text-[#A1A1AA]"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${operando ? "bg-[#16A34A]" : "bg-[#A1A1AA]"}`} />{operando ? "Operando" : "Sin turno"}
        </span>
      </div>
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-3">
        {esEstancia && <Mini icon={Car} value={s.autos_en_patio} label="autos en patio" tid={`sc-patio-${s.id_sucursal}`} />}
        {s.capacidad != null && <Mini icon={ParkingCircle} value={s.disponibles} label="disponibles" tid={`sc-disp-${s.id_sucursal}`} />}
        {esValet && <Mini icon={Users} value={s.valet_activos} label="valet activos" tid={`sc-valet-${s.id_sucursal}`} />}
        {esValet && (
          <Mini icon={ClipboardList} value={s.valet_solicitado} label="solicitados"
            alert={s.solicitados_con_retraso > 0} alertSub={`${s.solicitados_con_retraso} retrasado`} tid={`sc-solic-${s.id_sucursal}`} />
        )}
        {esValet && <Mini icon={PackageCheck} value={s.valet_en_entrega} label="en entrega" tid={`sc-entrega-${s.id_sucursal}`} />}
        <Mini icon={Wallet} value={s.cajas_abiertas} label="caja abierta" tid={`sc-caja-${s.id_sucursal}`} />
      </div>
    </div>
  );
}

/* ---------- Panel lateral: detalle de solicitados ---------- */
function SolicitadosPanel({ open, onOpenChange, filtro }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtro && filtro !== "all") params.set("id_sucursal", filtro);
      const { data } = await api.get(`/dashboard/solicitados?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);
  useEffect(() => {
    if (!open) return;
    const t1 = setInterval(() => setNowMs(Date.now()), 1000);
    const t2 = setInterval(cargar, REFRESH_MS);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [open, cargar]);

  const items = data?.items || [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="solicitados-panel">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#e4e4e7]">
          <SheetTitle className="flex items-center gap-2 font-display text-xl font-black text-[#0A0A0A]">
            <ClipboardList className="h-5 w-5 text-[#B45309]" /> Detalle de vehículos solicitados
          </SheetTitle>
          <SheetDescription>Vehículos que han sido solicitados para entrega.</SheetDescription>
        </SheetHeader>
        <div className="p-6 space-y-4">
          <div className="rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] px-4 py-3 flex items-start gap-2" data-testid="solicitados-resumen">
            <Info className="h-4 w-4 text-[#2563EB] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-bold text-[#1E3A8A]">Total de solicitados: {data?.total ?? 0}</p>
              <p className="text-xs text-[#3B82F6]">{data?.con_retraso ?? 0} vehículo(s) con retraso en la entrega.</p>
            </div>
          </div>
          {loading && !data ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#B45309]" /></div>
          ) : items.length === 0 ? (
            <p className="text-center text-sm text-[#A1A1AA] py-16">No hay vehículos solicitados en este momento.</p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-[#e4e4e7]">
              <table className="w-full text-sm" data-testid="solicitados-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-[#A1A1AA] bg-[#FAFAFA] border-b border-[#e4e4e7]">
                    <th className="px-3 py-2 font-bold">Sucursal</th>
                    <th className="px-3 py-2 font-bold">Folio</th>
                    <th className="px-3 py-2 font-bold">Vehículo / Placa</th>
                    <th className="px-3 py-2 font-bold">Solicitado hace</th>
                    <th className="px-3 py-2 font-bold">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const mins = it.solicitado_at ? minsDesde(it.solicitado_at, nowMs) : it.minutos_espera;
                    const tier = slaTier(mins);
                    const veh = [it.vehiculo, it.placa].filter(Boolean).join(" · ") || it.tipo_vehiculo || "—";
                    return (
                      <tr key={it.id_detalle} data-testid={`solic-row-${it.id_detalle}`} data-tier={tier}
                        className={`border-b border-[#f4f4f5] last:border-0 ${tier === "red" ? "bg-[#FEF2F2]" : ""}`}>
                        <td className="px-3 py-2.5 font-semibold text-[#3F3F46]">{it.sucursal}</td>
                        <td className="px-3 py-2.5 font-mono text-[#52525B]">{it.folio || "—"}</td>
                        <td className="px-3 py-2.5 text-[#3F3F46]">{veh}</td>
                        <td className={`px-3 py-2.5 tabular-nums font-bold ${tier === "red" ? "text-[#DC2626]" : "text-[#3F3F46]"}`}>{mins} min</td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-bold ${tierBadge[tier]}`}>{slaLabel(tier)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function DashboardResumen() {
  const [tab, setTab] = useState("operacion");
  const [sucursales, setSucursales] = useState([]);
  const [sucFiltro, setSucFiltro] = useState("all");
  const [data, setData] = useState(null);
  const [ingresos, setIngresos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [, setTick] = useState(0); // re-render para "hace X s"
  const [panelOpen, setPanelOpen] = useState(false);
  const [cortPanelOpen, setCortPanelOpen] = useState(false);
  const [gastosPanelOpen, setGastosPanelOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [modelo, setModelo] = useState("all");
  const filtroRef = useRef(sucFiltro);
  filtroRef.current = sucFiltro;
  const tabRef = useRef(tab);
  tabRef.current = tab;

  useEffect(() => { listSucursales().then(({ data }) => setSucursales(data || [])).catch(() => {}); }, []);

  const cargar = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroRef.current !== "all") params.set("id_sucursal", filtroRef.current);
      const qs = params.toString();
      if (tabRef.current === "ingresos") {
        const { data } = await api.get(`/dashboard/ingresos-hoy?${qs}`);
        setIngresos(data);
      } else {
        const { data } = await api.get(`/dashboard/operacion-ahora?${qs}`);
        setData(data);
      }
      setLastUpdate(new Date());
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { setExpanded(false); if (tab !== "historial") cargar(false); }, [sucFiltro, tab, cargar]);

  // Auto-refresh cada 30s (se pausa si la pestaña del navegador está oculta) + reloj de "hace X s".
  useEffect(() => {
    if (tab === "historial") return;
    const tickId = setInterval(() => setTick((n) => n + 1), 1000);
    const refId = setInterval(() => { if (!document.hidden) cargar(true); }, REFRESH_MS);
    return () => { clearInterval(tickId); clearInterval(refId); };
  }, [tab, cargar]);

  const t = data?.totales;
  const rows = data?.sucursales || [];
  const retrasoMin = data?.retraso_minutos ?? 15;

  // Prioriza sucursales con alerta; luego alfabético.
  const ordenadas = [...rows].sort((a, b) => (b.tiene_alerta - a.tiene_alerta) || a.nombre.localeCompare(b.nombre));
  const filtradas = ordenadas.filter((s) =>
    (modelo === "all" || s.modelo_cobro === modelo) &&
    (!search.trim() || s.nombre.toLowerCase().includes(search.trim().toLowerCase()))
  );
  const visibles = expanded ? filtradas : ordenadas.slice(0, INITIAL_CARDS);
  const restantes = ordenadas.length - visibles.length;

  return (
    <DashboardLayout>
      <div data-testid="dashboard-resumen" className="space-y-6 overflow-x-hidden">
        <div>
          <h1 className="font-display text-3xl font-black text-[#0A0A0A]">Dashboard <span className="text-[#A1A1AA]">/ Resumen</span></h1>
          <p className="text-sm text-[#71717A]">Aquí tienes la operación en tiempo real de tus estacionamientos.</p>
        </div>

        {/* Navegación superior */}
        <div className="flex flex-wrap gap-1 border-b border-[#e4e4e7]">
          {TABS.map((tb) => {
            const Icon = tb.icon; const active = tab === tb.key;
            return (
              <button key={tb.key} data-testid={`dashboard-tab-${tb.key}`}
                onClick={() => { if (!tb.ready) toast.info("Esta sección estará disponible próximamente"); setTab(tb.key); }}
                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-bold transition-colors ${active ? "text-[#0A0A0A]" : "text-[#A1A1AA] hover:text-[#52525B]"}`}>
                <Icon className="h-4 w-4" strokeWidth={2.3} /> {tb.label}
                {!tb.ready && <span className="ml-1 rounded-full bg-[#F4F4F5] border border-[#e4e4e7] px-1.5 py-0.5 text-[9px] tracking-wide uppercase font-bold text-[#A1A1AA]">Pronto</span>}
                {active && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-[#FFCC00]" />}
              </button>
            );
          })}
        </div>

        {tab === "historial" ? (
          <HistorialTab sucursales={sucursales} />
        ) : (
          <>
            {/* Controles */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Select value={sucFiltro} onValueChange={setSucFiltro}>
                  <SelectTrigger data-testid="dashboard-sucursal-select" className="w-[240px] bg-white"><SelectValue placeholder="Todas las sucursales" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="dashboard-suc-opt-all">Todas las sucursales</SelectItem>
                    {sucursales.map((s) => <SelectItem key={s.id_sucursal} value={s.id_sucursal}>{s.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="hidden sm:inline-flex items-center rounded-lg border border-[#e4e4e7] bg-white px-3 h-9 text-sm font-bold text-[#3F3F46]">Hoy</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="hidden sm:inline text-xs text-[#71717A]" data-testid="dashboard-last-update">
                  <span className="inline-block h-2 w-2 rounded-full bg-[#16A34A] mr-1 align-middle" /> Última actualización {fmtHace(lastUpdate)}
                </span>
                <button data-testid="dashboard-refresh-btn" onClick={() => cargar(true)} disabled={refreshing || loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-4 py-2 text-sm font-bold text-white hover:bg-[#27272A] transition-colors disabled:opacity-60">
                  <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Actualizar ahora
                </button>
              </div>
            </div>
            <p className="text-[11px] text-[#A1A1AA] -mt-3">Actualización automática cada 30 s.</p>

            {tab === "operacion" ? (loading && !data ? (
              <div className="flex items-center justify-center py-24" data-testid="dashboard-loading"><Loader2 className="h-8 w-8 animate-spin text-[#B45309]" /></div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="space-y-6">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-[#FFCC00] fill-[#FFCC00]" />
                  <div>
                    <h2 className="font-display text-lg font-black text-[#0A0A0A] leading-none">Operación ahora</h2>
                    <p className="text-xs text-[#A1A1AA]">Estado actual de la operación en todos tus estacionamientos.</p>
                  </div>
                </div>

                {/* Tarjetas KPI */}
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3">
                  <KpiCard testid="kpi-autos-patio" icon={Car} label="Autos en patio" value={t?.autos_en_patio ?? 0} sub="vehículos actualmente" />
                  <KpiCard testid="kpi-valet-activos" icon={Users} label="Valet activos" value={t?.valet_activos ?? 0} sub="en operación" />
                  <KpiCard testid="kpi-solicitados" icon={ClipboardList} label="Solicitados" value={t?.valet_solicitado ?? 0}
                    sub={(t?.solicitados_con_retraso ?? 0) > 0 ? `${t.solicitados_con_retraso} retrasado` : "sin retraso"}
                    subTone={(t?.solicitados_con_retraso ?? 0) > 0 ? "alert" : "muted"}
                    alert={(t?.solicitados_con_retraso ?? 0) > 0}
                    onClick={() => setPanelOpen(true)} />
                  <KpiCard testid="kpi-entregados-hoy" icon={PackageCheck} label="Entregados hoy" value={t?.entregados_hoy ?? 0} sub="acumulado del día" />
                  <KpiCard testid="kpi-ingresados-hoy" icon={LogIn} label="Ingresados hoy">
                    <p className="mt-2 font-display text-4xl font-black leading-none text-[#0A0A0A]" data-testid="kpi-ingresados-hoy-value">{t?.ingresados_hoy ?? 0}</p>
                    <p className="mt-1.5 text-xs font-semibold text-[#71717A]" data-testid="kpi-ingresados-hoy-desglose">
                      Parking {t?.ingresados_hoy_parking ?? 0} · Valet {t?.ingresados_hoy_valet ?? 0}
                    </p>
                  </KpiCard>
                  <KpiCard testid="kpi-cajas" icon={Wallet} label="Cajas abiertas" value={t?.cajas_abiertas ?? 0} sub={`de ${t?.sucursales ?? 0} sucursal(es)`} />
                  {t?.capacidad != null ? (
                    <KpiCard testid="kpi-ocupacion" icon={Gauge} label="Ocupación">
                      <div className="flex items-center gap-3">
                        <DonutRing pct={t.capacidad > 0 ? Math.round(Math.min(100, t.ocupacion / t.capacidad * 100)) : 0} />
                        <div>
                          <p className="font-display text-xl font-black text-[#0A0A0A] leading-none">{t.ocupacion}/{t.capacidad}</p>
                          <p className="text-[11px] text-[#71717A]">{t.disponibles} lugares</p>
                        </div>
                      </div>
                    </KpiCard>
                  ) : (
                    <KpiCard testid="kpi-ocupacion" icon={Gauge} label="Ocupación" value={t?.ocupacion ?? 0} sub="sin aforo configurado" />
                  )}
                </div>

                {/* Estados de valet */}
                <SegmentedBar t={t} />

                {/* Sucursales en este momento */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="h-5 w-5 text-[#B45309]" />
                    <h2 className="font-display text-lg font-black text-[#0A0A0A] leading-none">Sucursales en este momento</h2>
                  </div>
                  <p className="text-xs text-[#A1A1AA] mb-4">Resumen operativo por sucursal.</p>

                  {expanded && (
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A1A1AA]" />
                        <Input data-testid="suc-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar sucursal…" className="pl-9 bg-white" />
                      </div>
                      <Select value={modelo} onValueChange={setModelo}>
                        <SelectTrigger data-testid="suc-modelo-filter" className="w-[190px] bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los modelos</SelectItem>
                          <SelectItem value="COMBINADO">Combinado</SelectItem>
                          <SelectItem value="SOLO_ESTANCIA">Solo estancia</SelectItem>
                          <SelectItem value="SOLO_VALET">Solo valet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {visibles.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-[#d4d4d8] bg-white p-8 text-center text-sm text-[#A1A1AA]">Sin sucursales que mostrar.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="dashboard-sucursales-grid">
                      {visibles.map((s) => <SucursalCard key={s.id_sucursal} s={s} />)}
                    </div>
                  )}

                  {!expanded && restantes > 0 && (
                    <button data-testid="ver-todas-btn" onClick={() => setExpanded(true)}
                      className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#e4e4e7] bg-white px-4 py-2 text-sm font-bold text-[#3F3F46] hover:bg-[#FAFAFA] transition-colors">
                      Ver todas las sucursales ({ordenadas.length}) <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                  {expanded && (
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs text-[#A1A1AA]" data-testid="suc-filtradas-count">{filtradas.length} de {ordenadas.length} sucursales</span>
                      <button data-testid="ver-menos-btn" onClick={() => { setExpanded(false); setSearch(""); setModelo("all"); }}
                        className="inline-flex items-center gap-1 text-sm font-bold text-[#52525B] hover:text-[#0A0A0A]">
                        <X className="h-4 w-4" /> Ver menos
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            )) : (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="space-y-6">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-[#B45309]" strokeWidth={2.4} />
                  <div>
                    <h2 className="font-display text-lg font-black text-[#0A0A0A] leading-none">Ingresos de hoy</h2>
                    <p className="text-xs text-[#A1A1AA]">Dinero efectivamente cobrado durante el día de hoy.</p>
                  </div>
                </div>
                <IngresosContent data={ingresos} onOpenCortesias={() => setCortPanelOpen(true)} onOpenGastos={() => setGastosPanelOpen(true)} />
              </motion.div>
            )}
          </>
        )}
      </div>

      <SolicitadosPanel open={panelOpen} onOpenChange={setPanelOpen} filtro={sucFiltro} />
      <CortesiasPanel open={cortPanelOpen} onOpenChange={setCortPanelOpen} filtro={sucFiltro} />
      <GastosHoyPanel open={gastosPanelOpen} onOpenChange={setGastosPanelOpen} filtro={sucFiltro} />
    </DashboardLayout>
  );
}
